import React from 'react';
import { Capacitor } from '@capacitor/core';
import { Box, Divider, Radio, RadioGroup, Text, VStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { UserLocale } from 'clipsal-cortex-types/src/api/api-user';

import { SEHomeCard } from '../../../common/components/SEHomeCard';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { IS_NATIVE } from '../../../common/constants';
import { SUPPORTED_LOCALES } from '../../../i18n';
import { setupPushNotifications } from '../../../utils/native/push-notifications';
import { useUpdateLocaleMutation } from '../../account/accountApi';

function mapLocaleToApiValue(locale: string): UserLocale {
  switch (locale) {
    case 'en-US':
      return 'EN_US';
    case 'fr-CA':
      return 'FR_CA';
    case 'es-US':
      return 'ES_US';
    default:
      return 'EN_US';
  }
}

export function SettingsLanguage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [updateLocale] = useUpdateLocaleMutation();

  // Static `t()` references so the language names are translated into the active language.
  // Keep these literal (not `t(`Languages.${code}`)`) so the i18n-unused validator can detect them.
  const localeLabels: Record<string, string> = {
    'en-US': t('Languages.en-US'),
    'fr-CA': t('Languages.fr-CA'),
    'es-US': t('Languages.es-US'),
  };

  async function handleChangeLanguage(locale: string) {
    try {
      i18n.changeLanguage(locale);
      const userLocale = mapLocaleToApiValue(locale);
      await updateLocale({ locale: userLocale }).unwrap();

      // Re-register push notifications with the new locale so backend sends notifications in the correct language
      if (IS_NATIVE && Capacitor.isNativePlatform()) {
        await setupPushNotifications(navigate, userLocale).catch((error) => {
          console.error('Failed to re-register push notifications after language change:', error);
        });
      }
    } catch (e) {
      console.error(e);
      Sentry.captureException(e, {
        tags: {
          category: 'settings',
          component: 'SettingsLanguage',
          method: 'handleChangeLanguage',
        },
        extra: { locale },
      });
    }
  }

  return (
    <SlidingAnimationPageBase title={t('Common.language')} backURL={`../home?direction=back`}>
      <Box px={5}>
        <Text pt={5} pb={2} fontSize="sm" color="gray.500">
          {t('Common.language').toUpperCase()}
        </Text>
        <SEHomeCard borderTopLeftRadius="5px" p={0}>
          <RadioGroup data-testid="language-selector" value={i18n.language} onChange={handleChangeLanguage}>
            <VStack align="stretch" spacing={0} divider={<Divider borderColor="rgba(151, 151, 151, 0.3)" />}>
              {Object.keys(SUPPORTED_LOCALES).map((code) => (
                <Radio
                  key={code}
                  data-testid={`${code}-radio-option`}
                  value={code}
                  size="lg"
                  w="full"
                  px={4}
                  py={4}
                  variant="spaced"
                >
                  <Text fontSize={16}>{localeLabels[code]}</Text>
                </Radio>
              ))}
            </VStack>
          </RadioGroup>
        </SEHomeCard>
      </Box>
    </SlidingAnimationPageBase>
  );
}
